// BUILD-046 section C (Owner direction of 2026-10-21, Update task and follow-up accountability): the ONE compact Update form.
//
// Status, what happened (the progress note, with the activity date), the next action, the next follow-up date and the Task due date - each its own labelled field, none
// derived from another. ONE Save sends one request (POST /work/{id}/progress), so the note and the field changes are saved together or not at all. The rules are
// updateTaskModel.ts (mirroring the server, which stays the authority and whose words are shown when it refuses). When a save is refused or fails, NOTHING typed is lost:
// the form stays open with the draft and the message beside the field it is about.
//
// A note alone never moves a date or clears a missed follow-up. A missed follow-up is named, and it is resolved only by choosing a new date or clearing it (with the
// reason every change of an existing date needs).
import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { workApi } from "../../api/workShared";
import type { WorkProgressInput, WorkProgressResponse } from "../../api/workShared";
import { ACTIVITY_DATE_LABEL, NEXT_ACTION_LABEL, NEXT_FOLLOW_UP_DATE_LABEL, TASK_DUE_DATE_LABEL, phoenixToday } from "./dateLabels";
import { DictationButton } from "./DictationButton";
import { QuickDateField } from "./QuickDateField";
import { stripTestMarker } from "./displayNames";
import { formatDueDate } from "./taskRowModel";
import { usePhoenixDay } from "./usePhoenixDay";
import { RequestedBy } from "./RequestedBy";
import { UPDATE_DEADLINE_LOCKED_LINE, UPDATE_STATUSES, UPDATE_TASK_HELP_LINE, followUpState, initialUpdateDraft, issuesFromError, noteLabel, replacedDates, updateBody, validateUpdate } from "./updateTaskModel";
import type { UpdateDraft, UpdateField, UpdateIssue, UpdateStatus, UpdateTaskView } from "./updateTaskModel";

export interface UpdateTaskFormProps {
  task: UpdateTaskView & { id: string; title: string };
  /** The server accepted the Update; the host merges the answer, closes the form and refreshes what depends on it. */
  onSaved: (response: WorkProgressResponse) => void;
  onCancel: () => void;
  /** The request, replaceable so a check can drive the form without a server. */
  send?: (id: string, body: WorkProgressInput) => Promise<WorkProgressResponse>;
  /**
   * BUILD-046 G4 (October 23 addendum, item 1): true only when the person may change the Task due date - the person who issued the task (a task issued to oneself included), by the same rule that offers Edit task. Absent or false: the due date is
   * shown as a fixed value with the reason and is neither offered nor sent. The server refuses a request that names it either way.
   */
  canChangeDeadline?: boolean;
}

const INPUT = "w-full px-3 py-2 border rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4AABDE] disabled:opacity-60";
const LABEL = "mb-1 block text-xs font-medium text-gray-700";
const DAY = (date: string): string => formatDueDate(date) ?? date;

export function UpdateTaskForm({ task, onSaved, onCancel, send, canChangeDeadline = false }: UpdateTaskFormProps) {
  const pageDay = usePhoenixDay();
  const [draft, setDraft] = useState<UpdateDraft>(() => initialUpdateDraft(task, phoenixToday()));
  const [issues, setIssues] = useState<UpdateIssue[]>([]);
  const [busy, setBusy] = useState(false);
  // Owner direction 2026-10-23: the Task due date is shown as a plain value here with a compact Change (or Set) action, so this form is about progress and not a second Edit form. The date field opens
  // when the person asks for it, when the draft already differs from the saved date, or when the server or the form has a message about it. Nothing about how the date is saved changes.
  const originalDue = useRef<string>(draft.dueDate).current;
  const [changingDue, setChangingDue] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const shownTitle = stripTestMarker(task.title);
  const idBase = "update-" + task.id;
  const set = <K extends keyof UpdateDraft>(key: K, value: UpdateDraft[K]): void => {
    setDraft((current) => ({ ...current, [key]: value }));
    setIssues((current) => current.filter((issue) => issue.field !== key && issue.field !== "form"));
  };
  const messageOf = (field: UpdateField): string | null => issues.find((issue) => issue.field === field)?.message ?? null;
  const formMessages = issues.filter((issue) => issue.field === "form");
  const missed = followUpState(task.followUpDate, pageDay) === "missed" && draft.followUpDate === (typeof task.followUpDate === "string" ? task.followUpDate : "");
  const replaced = replacedDates(task, draft, canChangeDeadline);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    const today = phoenixToday();
    const found = validateUpdate(task, draft, today, canChangeDeadline);
    if (found.length > 0) {
      setIssues(found);
      const first = found[0].field;
      const target = first === "form" ? null : formRef.current?.querySelector<HTMLElement>("[data-field=\"" + first + "\"]");
      target?.focus();
      return;
    }
    const body = updateBody(task, draft, today, canChangeDeadline);
    if (body === null) return;
    setBusy(true);
    setIssues([]);
    try {
      const response = await (send ?? ((id: string, input: WorkProgressInput) => workApi.progress(id, input)))(task.id, body);
      onSaved(response);
    } catch (thrown) {
      // Nothing is cleared: the draft stays exactly as typed and the server words appear beside the field they are about.
      setIssues(issuesFromError(thrown));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form ref={formRef} onSubmit={(event) => { void submit(event); }} aria-label={"Update: " + shownTitle} data-testid="update-task-form" className="space-y-3 rounded-lg border border-[#4AABDE] bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Update: {shownTitle}</h3>
          <p className="text-xs text-gray-500" data-testid="update-task-help">{UPDATE_TASK_HELP_LINE}</p>
        </div>
        {/* Pass F1: the original requester, the same line as in Edit task, independent of the list's Show requester setting. */}
        <RequestedBy item={task} testId="update-requested-by" />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor={idBase + "-status"} className={LABEL}>Status</label>
          <select id={idBase + "-status"} data-field="status" value={draft.status} disabled={busy} onChange={(e) => set("status", e.target.value as UpdateStatus)} className={INPUT + " border-gray-300 bg-white"}>
            {UPDATE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {messageOf("status") !== null && <p role="alert" className="mt-1 text-xs text-red-700">{messageOf("status")}</p>}
        </div>
        <div>
          <label htmlFor={idBase + "-activity"} className={LABEL}>{ACTIVITY_DATE_LABEL}</label>
          <input id={idBase + "-activity"} data-field="activityDate" type="date" value={draft.activityDate} max={pageDay} disabled={busy} aria-invalid={messageOf("activityDate") !== null} onChange={(e) => set("activityDate", e.target.value)} className={INPUT + (messageOf("activityDate") !== null ? " border-red-500" : " border-gray-300")} />
          <p className="mt-1 text-xs text-gray-500">The day the work happened. It can be earlier, never later.</p>
          {messageOf("activityDate") !== null && <p role="alert" className="mt-1 text-xs text-red-700">{messageOf("activityDate")}</p>}
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between gap-2">
          <label htmlFor={idBase + "-note"} className="block text-xs font-medium text-gray-700">{noteLabel(task, draft)}</label>
          <DictationButton value={draft.note} onChange={(text) => set("note", text)} disabled={busy} />
        </div>
        <textarea id={idBase + "-note"} data-field="note" rows={3} value={draft.note} disabled={busy} aria-invalid={messageOf("note") !== null} onChange={(e) => set("note", e.target.value)} className={INPUT + (messageOf("note") !== null ? " border-red-500" : " border-gray-300")} />
        {messageOf("note") !== null && <p role="alert" className="mt-1 text-xs text-red-700">{messageOf("note")}</p>}
      </div>

      {missed && (
        <p role="note" data-testid="missed-follow-up-note" className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          The follow-up on {DAY(draft.followUpDate)} was missed. Saving a note does not change it. Choose a new follow-up date below{draft.status === "waiting" || draft.status === "blocked" ? "." : ", or clear it."}
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor={idBase + "-next"} className={LABEL}>{NEXT_ACTION_LABEL}</label>
          <input id={idBase + "-next"} data-field="nextAction" type="text" value={draft.nextAction} disabled={busy} aria-invalid={messageOf("nextAction") !== null} onChange={(e) => set("nextAction", e.target.value)} className={INPUT + (messageOf("nextAction") !== null ? " border-red-500" : " border-gray-300")} />
          {messageOf("nextAction") !== null && <p role="alert" className="mt-1 text-xs text-red-700">{messageOf("nextAction")}</p>}
        </div>
        <div data-field="followUpDate" tabIndex={-1} className="focus:outline-none">
          <QuickDateField idPrefix={idBase + "-follow"} label={NEXT_FOLLOW_UP_DATE_LABEL} value={draft.followUpDate} onChange={(value) => set("followUpDate", value)} disabled={busy} layout="stacked" />
          {messageOf("followUpDate") !== null && <p role="alert" className="mt-1 text-xs text-red-700">{messageOf("followUpDate")}</p>}
        </div>
        <div data-field="dueDate" tabIndex={-1} className="focus:outline-none" data-testid="update-due-date">
          {!canChangeDeadline ? (
            <div data-testid="update-due-locked">
              <span className={LABEL}>{TASK_DUE_DATE_LABEL}</span>
              <div className="py-1.5 text-sm text-gray-900" data-testid="update-due-value">{originalDue === "" ? "No due date" : DAY(originalDue)}</div>
              <p className="text-xs text-gray-500" data-testid="update-due-locked-reason">{UPDATE_DEADLINE_LOCKED_LINE}</p>
            </div>
          ) : changingDue || draft.dueDate !== originalDue || messageOf("dueDate") !== null ? (
            <>
              <QuickDateField idPrefix={idBase + "-due"} label={TASK_DUE_DATE_LABEL} value={draft.dueDate} onChange={(value) => set("dueDate", value)} disabled={busy} layout="stacked" />
              <button type="button" data-testid="update-due-keep" disabled={busy} onClick={() => { set("dueDate", originalDue); setChangingDue(false); }} className="mt-1 text-xs text-[#1f6fa5] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50">{originalDue === "" ? "Keep no due date" : "Keep " + DAY(originalDue)}</button>
            </>
          ) : (
            <div>
              <span className={LABEL}>{TASK_DUE_DATE_LABEL}</span>
              <div className="flex items-center gap-2 py-1.5 text-sm text-gray-900">
                <span data-testid="update-due-value">{originalDue === "" ? "No due date" : DAY(originalDue)}</span>
                <button type="button" data-testid="update-due-change" disabled={busy} aria-label={(originalDue === "" ? "Set" : "Change") + " Task due date"} onClick={() => setChangingDue(true)} className="text-xs text-[#1f6fa5] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50">{originalDue === "" ? "Set" : "Change"}</button>
              </div>
            </div>
          )}
          {messageOf("dueDate") !== null && <p role="alert" className="mt-1 text-xs text-red-700">{messageOf("dueDate")}</p>}
        </div>
      </div>

      {replaced.length > 0 && (
        <div data-testid="date-change-reason">
          <label htmlFor={idBase + "-reason"} className={LABEL}>Why is the date changing? (required)</label>
          <input id={idBase + "-reason"} data-field="reason" type="text" value={draft.reason} disabled={busy} aria-invalid={messageOf("reason") !== null} onChange={(e) => set("reason", e.target.value)} className={INPUT + (messageOf("reason") !== null ? " border-red-500" : " border-gray-300")} />
          <p className="mt-1 text-xs text-gray-500">{replaced.map((change) => change.label + ": " + DAY(change.from) + " to " + (change.to === "" ? "no date" : DAY(change.to))).join(". ")}. The old and new dates and this reason are kept in the task history.</p>
          {messageOf("reason") !== null && <p role="alert" className="mt-1 text-xs text-red-700">{messageOf("reason")}</p>}
        </div>
      )}

      {formMessages.length > 0 && (
        <div role="alert" data-testid="update-task-error" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {formMessages.map((issue) => <p key={issue.message}>{issue.message}</p>)}
          <p className="mt-1 text-xs text-red-800">Nothing you typed was lost.</p>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="rounded px-4 py-2 text-sm text-gray-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50">Cancel</button>
        <button type="submit" disabled={busy} data-testid="update-task-save" className="rounded bg-[#1f6fa5] px-4 py-2 text-sm font-semibold text-white hover:bg-[#195d8a] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#2a7fb8] disabled:opacity-60">{busy ? "Saving..." : "Save update"}</button>
      </div>
    </form>
  );
}
